/**
 * Čistá logika prohlížeče fotek (`PhotoViewer.tsx`): klávesa → akce, krok vpřed a vzad,
 * rozhodnutí o smazání, index po smazání, sladění indexu s ID fotky, legenda kláves a fronta
 * uložení ★.
 * Bez Reactu a bez `window` (projekt `admin` ve Vitestu běží v `node`).
 *
 * Klávesy se čtou JEN z `PHOTO_VIEWER_KEYS`; literál klávesy smí jen mapa glyfů `KEY_GLYPHS`.
 */
import type { PickerTileState } from "./picker/pickerModel";
import { PHOTO_VIEWER_KEYS, type PhotoViewerKeyAction } from "./PhotoViewer.types";

export type PhotoViewerAction = PhotoViewerKeyAction;

/** Režim prohlížeče: stránka Média (`"library"`), nebo okno výběru fotek (`"picker"`, řez 05). */
export type PhotoViewerViewMode = "library" | "picker";

const ACTIONS = Object.keys(PHOTO_VIEWER_KEYS) as PhotoViewerAction[];

/** Akce, které držená klávesa (`repeat`) nesmí opakovat. */
const NO_REPEAT: ReadonlySet<PhotoViewerAction> = new Set(["star", "delete"]);

/**
 * Akce na klávese, kterou tlačítko nebo odkaz s fokusem aktivuje samo (detail na Enter):
 * nad nimi prohlížeč klávesu nechá prohlížeči, jinak by Enter na „Zavřít“ otevřel detail.
 */
const NATIVE_ACTIVATION: ReadonlySet<PhotoViewerAction> = new Set(["detail"]);

export interface ViewerKeyEvent {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  repeat: boolean;
  isComposing?: boolean;
  /** Událost míří do input, textarea, select nebo `contentEditable`. */
  editableTarget: boolean;
  /** Událost míří na tlačítko nebo odkaz (Enter ho aktivuje nativně). */
  buttonTarget: boolean;
}

/**
 * Akce stisknuté klávesy, nebo `null`: s modifikátorem (Cmd+F hledá v prohlížeči,
 * Cmd+Backspace maže v poli), při skládání znaku, v editovatelném poli, u ★ a smazání
 * při držené klávese a u detailu nad tlačítkem nebo odkazem s fokusem.
 */
export function viewerActionForKey(ev: ViewerKeyEvent): PhotoViewerAction | null {
  if (ev.metaKey || ev.ctrlKey || ev.altKey || ev.isComposing || ev.editableTarget) return null;
  const action = ACTIONS.find((a) => (PHOTO_VIEWER_KEYS[a] as readonly string[]).includes(ev.key));
  if (!action) return null;
  if (ev.repeat && NO_REPEAT.has(action)) return null;
  if (ev.buttonTarget && NATIVE_ACTIVATION.has(action)) return null;
  return action;
}

/** Režim prohlížeče z jeho props: bez `mode` je to stránka Média. */
export function viewerModeOf(props: { mode?: PhotoViewerViewMode }): PhotoViewerViewMode {
  return props.mode ?? "library";
}

/** Akce, které nabízí jen knihovna: okno výběru (řez 05) fotky nemaže a detail nemá (AK9). */
const LIBRARY_ONLY: ReadonlySet<PhotoViewerAction> = new Set(["delete", "detail"]);

/**
 * Jediné místo, které rozhoduje, co režim nabízí: čte ho legenda, posluchač kláves, `run`,
 * `requestDelete` i tlačítka smazání a detailu. Ve výběru chybí smazání a detail, ostatní
 * akce jsou v obou režimech.
 */
export function actionAvailable(mode: PhotoViewerViewMode, action: PhotoViewerAction): boolean {
  return mode !== "picker" || !LIBRARY_ONLY.has(action);
}

/**
 * Tlačítko „Vybrat“ prohlížeče v okně výběru podle stavu fotky (řez 05, AK9): vybraná
 * a aktuální fotka ukazují „Vybráno“ (stisknuté, klik výběr zruší, resp. vybere tutéž fotku),
 * fotka už v galerii vybrat nejde.
 */
export function pickButton(state: PickerTileState): {
  label: "pick" | "picked";
  pressed: boolean;
  disabled: boolean;
} {
  switch (state) {
    case "selectable":
      return { label: "pick", pressed: false, disabled: false };
    case "selected":
    case "current":
      return { label: "picked", pressed: true, disabled: false };
    case "in-gallery":
      return { label: "pick", pressed: false, disabled: true };
  }
}

/** Krok vpřed: posun, donačtení další stránky, nebo stop na konci celkového počtu (necyklí). */
export function nextStep(p: {
  index: number;
  length: number;
  total: number;
  hasMore: boolean;
}): "move" | "load" | "stop" {
  if (p.index < p.length - 1) return "move";
  if (p.index === p.length - 1 && p.hasMore && p.length < p.total) return "load";
  return "stop";
}

/** Krok vzad: na první fotce stop (necyklí). */
export function prevStep(p: { index: number }): "move" | "stop" {
  return p.index > 0 ? "move" : "stop";
}

/** Nepoužitá fotka jde rovnou do koše, použitá se zeptá dialogem s počtem použití. */
export function deleteDecision(usageCount: number): "trash" | "confirm" {
  return usageCount > 0 ? "confirm" : "trash";
}

/**
 * Co po smazání, počítáno nad seznamem PŘED odebráním smazané fotky: `stay` (na místo
 * smazané přijde následující), `load` (smazaná byla poslední načtená a další stránka je),
 * `prev` (poslední v seznamu), `close` (jediná a nic dalšího).
 */
export function afterDelete(p: {
  index: number;
  length: number;
  hasMore: boolean;
}): "stay" | "load" | "prev" | "close" {
  if (p.length === 1 && !p.hasMore) return "close";
  if (p.index < p.length - 1) return "stay";
  if (p.hasMore) return "load";
  return "prev";
}

/** Paměť sladění mezi rendery (refy kontejneru). */
export interface ViewerIndexMemory {
  prevIndex: number | null;
  currentId: string | null;
}

/**
 * Sladí index s ID zobrazené fotky. Změněný index (šipka, donačtení, `onIndexChange` po
 * smazání, ořez od rodiče) je navigace a zapamatuje fotku na novém indexu (nebo `null`, dokud
 * stránka nedorazí). Při stejném indexu a změněném seznamu (přenačtení, filtr ★ fotku vyřadí,
 * přeřazení) vrátí index téže fotky; když v seznamu není, nejbližší platný index (nad prázdným
 * seznamem 0, ne −1).
 */
export function syncViewerIndex(
  prev: ViewerIndexMemory,
  next: { items: readonly { id: string }[]; index: number },
): { index: number; currentId: string | null } {
  const { items, index } = next;
  if (prev.prevIndex === null || index !== prev.prevIndex)
    return { index, currentId: items[index]?.id ?? null };
  if (prev.currentId === null) return { index, currentId: items[index]?.id ?? null };
  if (items[index]?.id === prev.currentId) return { index, currentId: prev.currentId };
  const found = items.findIndex((x) => x.id === prev.currentId);
  if (found >= 0) return { index: found, currentId: prev.currentId };
  const clamped = Math.max(0, Math.min(index, items.length - 1));
  return { index: clamped, currentId: items[clamped]?.id ?? null };
}

/** Zobrazení kláves v legendě; ostatní klávesy se ukazují, jak jsou. */
export const KEY_GLYPHS: Readonly<Record<string, string>> = {
  ArrowRight: "→",
  ArrowLeft: "←",
  Escape: "Esc",
};

function glyphOf(key: string): string {
  return KEY_GLYPHS[key] ?? (key.length === 1 ? key.toUpperCase() : key);
}

export interface LegendEntry {
  action: PhotoViewerAction;
  keys: readonly string[];
  glyphs: string[];
}

/** Řádky legendy v pořadí `PHOTO_VIEWER_KEYS`; glyfy bez duplicit (`f`/`F` → jedno „F“). */
export function legendEntries(
  keys: Readonly<Record<PhotoViewerAction, readonly string[]>> = PHOTO_VIEWER_KEYS,
): LegendEntry[] {
  return (Object.keys(keys) as PhotoViewerAction[]).map((action) => ({
    action,
    keys: keys[action],
    glyphs: [...new Set(keys[action].map(glyphOf))],
  }));
}

/** Nová hodnota ★: opak overridu (stisknuto, PATCH ještě běží), jinak opak uložené. */
export function nextStarValue(
  overrides: ReadonlyMap<string, boolean>,
  item: { id: string; isStarred: boolean },
): boolean {
  return !(overrides.get(item.id) ?? item.isStarred);
}

export interface StarToggleHandlers<T> {
  onSaved: (item: T, value: boolean) => void;
  onError: (err: unknown) => void;
}

/** Fronta uložení ★ (jedna pravda: overridy se odvozují z nevyřízených požadavků). */
export interface StarQueue<T extends { id: string; isStarred: boolean }> {
  /** Optimistická ★ fotek, jejichž PATCH ještě běží. */
  overrides(): ReadonlyMap<string, boolean>;
  /**
   * Přepne ★; požadavky jedné fotky jdou řetězem v pořadí stisků. `handlers` patří tomuto
   * stisku (volající předá aktuální zpětná volání, fronta je nedrží).
   */
  toggle(item: T, handlers: StarToggleHandlers<T>): void;
  /** Splní se, až doběhnou všechny požadavky fotky `id` (hned, když žádný neběží). */
  settled(id: string): Promise<void>;
}

/**
 * Fronta ★: dvojí rychlé stisknutí odešle dva PATCH za sebou a skončí na původní hodnotě.
 * Override fotky platí, dokud nedoběhne její poslední požadavek; chyba override zahodí
 * (ukáže se uložená hodnota) a hlásí se přes `onError` stisku. `onOverrides` dostane novou mapu
 * po každé změně overridů.
 */
export function createStarQueue<T extends { id: string; isStarred: boolean }>(deps: {
  save: (id: string, value: boolean) => Promise<unknown>;
  onOverrides: (overrides: ReadonlyMap<string, boolean>) => void;
}): StarQueue<T> {
  const chains = new Map<string, { tail: Promise<void>; pending: number; value?: boolean }>();
  const overrides = (): ReadonlyMap<string, boolean> => {
    const out = new Map<string, boolean>();
    for (const [id, c] of chains) if (c.value !== undefined) out.set(id, c.value);
    return out;
  };
  const publish = () => deps.onOverrides(overrides());

  return {
    overrides,
    toggle(item, handlers) {
      const value = nextStarValue(overrides(), item);
      const chain = chains.get(item.id) ?? { tail: Promise.resolve(), pending: 0 };
      chains.set(item.id, chain);
      chain.value = value;
      chain.pending += 1;
      publish();
      chain.tail = chain.tail
        .then(() => deps.save(item.id, value))
        .then(
          () => handlers.onSaved(item, value),
          (err: unknown) => {
            chain.value = undefined;
            publish();
            handlers.onError(err);
          },
        )
        .finally(() => {
          chain.pending -= 1;
          if (chain.pending === 0) {
            chains.delete(item.id);
            publish();
          }
        });
    },
    settled(id) {
      return chains.get(id)?.tail ?? Promise.resolve();
    },
  };
}
